// Typed API client for the Protocol Worker.
//
// - Adds `Authorization: Bearer <token>` from `lib/auth.ts` on every request.
// - Parses the structured error envelope (`{error, detail, code, fields}`)
//   on non-2xx and throws an `ApiError` with `kind`/`detail`/etc the UI can
//   `switch (err.kind)` on.
// - Optional Zod schema parameter validates response shape — used at
//   boundaries where the contract matters (migrate import acks, ingest).
//
// Mirrors the speediance-proxy / protocol-api error envelope:
//   { error: <kind>, detail?: string, code?: string|number, fields?: [...] }

import type { z } from 'zod';
import { getApiUrl, getToken } from './auth';
import { ApiErrorEnvelopeSchema, type ApiErrorEnvelope } from './types';

export class ApiError extends Error {
  readonly kind: string;
  readonly detail?: string;
  readonly code?: string | number;
  readonly fields?: Array<{ path: string; message: string }>;
  readonly status: number;
  readonly envelope: ApiErrorEnvelope | null;

  constructor(args: {
    kind: string;
    status: number;
    detail?: string;
    code?: string | number;
    fields?: Array<{ path: string; message: string }>;
    envelope?: ApiErrorEnvelope | null;
  }) {
    super(args.detail || args.kind);
    this.name = 'ApiError';
    this.kind = args.kind;
    this.status = args.status;
    this.detail = args.detail;
    this.code = args.code;
    this.fields = args.fields;
    this.envelope = args.envelope ?? null;
  }
}

// Thrown when the network never reached the API (offline, DNS failure, CORS
// pre-flight blocked client-side, Worker not running). Distinguished from
// `ApiError` (a *response* failure) so the UI can render "couldn't reach…".
export class ApiNetworkError extends Error {
  override readonly cause: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiNetworkError';
    this.cause = cause;
  }
}

async function parseErrorEnvelope(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Worker may also produce non-JSON 502s from upstream — fall through with
    // an empty envelope.
  }
  const parsed = ApiErrorEnvelopeSchema.safeParse(body);
  if (parsed.success) {
    const env = parsed.data;
    return new ApiError({
      kind: env.error,
      status: res.status,
      detail: env.detail,
      code: env.code,
      fields: env.fields,
      envelope: env,
    });
  }
  return new ApiError({
    kind: res.status === 401 ? 'auth' : 'default',
    status: res.status,
    detail: `HTTP ${res.status}`,
  });
}

type RequestOptions<TOut> = {
  schema?: z.ZodType<TOut>;
  signal?: AbortSignal;
  /** LLM/proposal calls are retryable reads-in-spirit and should not enter the write queue. */
  stashOnNetworkError?: boolean;
};

async function request<TOut>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  opts: RequestOptions<TOut> = {},
): Promise<TOut> {
  const base = getApiUrl();
  const token = getToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
      // Cookies aren't part of the auth model — bearer token is the only credential.
      credentials: 'omit',
    });
  } catch (err) {
    // R17/A10: network failed → stash failed writes so the operator can retry
    // explicitly. Reads (GET) don't stash — they're safe to re-run on a refresh.
    if (method !== 'GET' && opts.stashOnNetworkError !== false) {
      stashPendingWrite({ method, path, body });
    }
    throw new ApiNetworkError(`Couldn't reach the Protocol API at ${base}`, err);
  }

  if (!res.ok) {
    throw await parseErrorEnvelope(res);
  }

  // 204 No Content — return `undefined` and let the call site's type assert it.
  if (res.status === 204) {
    return undefined as unknown as TOut;
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch (err) {
    throw new ApiNetworkError('Response was not valid JSON', err);
  }

  if (opts.schema) {
    const parsed = opts.schema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError({
        kind: 'validation_error',
        status: res.status,
        detail: `Response failed schema validation: ${parsed.error.message}`,
      });
    }
    return parsed.data;
  }
  return json as TOut;
}

export const apiClient = {
  get<TOut = unknown>(path: string, opts: RequestOptions<TOut> = {}): Promise<TOut> {
    return request<TOut>('GET', path, undefined, opts);
  },
  post<TOut = unknown>(path: string, body: unknown, opts: RequestOptions<TOut> = {}): Promise<TOut> {
    return request<TOut>('POST', path, body, opts);
  },
  put<TOut = unknown>(path: string, body: unknown, opts: RequestOptions<TOut> = {}): Promise<TOut> {
    return request<TOut>('PUT', path, body, opts);
  },
  delete<TOut = unknown>(path: string, opts: RequestOptions<TOut> = {}): Promise<TOut> {
    return request<TOut>('DELETE', path, undefined, opts);
  },
  /**
   * POST a binary body (e.g. a `.fit` file upload). Sends the bytes as-is
   * with `Content-Type: application/octet-stream` (overridable) plus any
   * caller-supplied custom headers. Same auth + error envelope handling as
   * `post` — non-2xx throws `ApiError`, network failure throws
   * `ApiNetworkError`. Unlike `post`, network failures are NOT stashed:
   * the body is binary and the failed-writes queue is JSON-shaped (the
   * operator just retries the file picker explicitly).
   */
  async postBinary<TOut = unknown>(
    path: string,
    body: ArrayBuffer,
    opts: {
      schema?: z.ZodType<TOut>;
      signal?: AbortSignal;
      contentType?: string;
      headers?: Record<string, string>;
    } = {},
  ): Promise<TOut> {
    const base = getApiUrl();
    const token = getToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': opts.contentType ?? 'application/octet-stream',
      ...(opts.headers ?? {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers,
        body,
        signal: opts.signal,
        credentials: 'omit',
      });
    } catch (err) {
      // Binary bodies aren't stashable in the JSON failed-writes queue, so we
      // just surface the network error and let the caller retry from the UI.
      throw new ApiNetworkError(`Couldn't reach the Protocol API at ${base}`, err);
    }

    if (!res.ok) {
      throw await parseErrorEnvelope(res);
    }

    if (res.status === 204) {
      return undefined as unknown as TOut;
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      throw new ApiNetworkError('Response was not valid JSON', err);
    }

    if (opts.schema) {
      const parsed = opts.schema.safeParse(json);
      if (!parsed.success) {
        throw new ApiError({
          kind: 'validation_error',
          status: res.status,
          detail: `Response failed schema validation: ${parsed.error.message}`,
        });
      }
      return parsed.data;
    }
    return json as TOut;
  },
  /**
   * GET a binary response (e.g. the LLM export zip). Returns the Blob and
   * the suggested filename parsed from `Content-Disposition` (or `null`
   * if the header is absent or unparseable). Same auth + error envelope
   * handling as `get` — non-2xx throws `ApiError`, network failure throws
   * `ApiNetworkError` (NOT stashed; GETs are safe to re-run on retry per
   * the existing failed-write policy).
   */
  async getBlob(
    path: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const base = getApiUrl();
    const token = getToken();
    const headers: Record<string, string> = { Accept: 'application/octet-stream' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method: 'GET',
        headers,
        signal: opts.signal,
        credentials: 'omit',
      });
    } catch (err) {
      throw new ApiNetworkError(`Couldn't reach the Protocol API at ${base}`, err);
    }

    if (!res.ok) {
      throw await parseErrorEnvelope(res);
    }

    const blob = await res.blob();
    const filename = parseFilenameFromContentDisposition(
      res.headers.get('content-disposition'),
    );
    return { blob, filename };
  },
};

// Extract `filename="…"` from a Content-Disposition header. Handles both
// quoted and unquoted forms; ignores `filename*=` RFC 5987 extensions
// (the server we control never sends them). Returns null on a malformed
// or missing header — caller can fall back to a default name.
function parseFilenameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  // Quoted form: filename="the-protocol-export-2026-05-26.zip"
  const quoted = value.match(/filename="([^"]+)"/);
  if (quoted) return quoted[1] ?? null;
  // Unquoted form: filename=foo.zip
  const bare = value.match(/filename=([^;]+)/);
  if (bare) return (bare[1] ?? '').trim() || null;
  return null;
}

// Render a thrown error into a single user-facing string. Switches on the
// envelope `kind` discriminator — matches the V0 `formatSpeedianceError`
// taxonomy in shape, narrower in scope (the Protocol API surface, not the
// Speediance proxy's).
export function formatApiError(err: unknown): string {
  if (err instanceof ApiNetworkError) return err.message;
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'auth':
        return "That token doesn't match. Generate a new one or check 1Password.";
      case 'forbidden_origin':
        return 'This origin is not allowed by the Protocol API. Check ALLOWED_ORIGIN on the Worker.';
      case 'cors_not_configured':
        return 'The Protocol API is missing its ALLOWED_ORIGIN environment variable.';
      case 'validation_error': {
        if (err.fields && err.fields.length > 0) {
          return `Validation: ${err.fields.map((f) => `${f.path} — ${f.message}`).join('; ')}`;
        }
        return err.detail || 'Validation failed.';
      }
      case 'not_found':
        return err.detail || 'Not found.';
      default:
        return err.detail || `Error (${err.kind})`;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

// ---- Failed-write local stash (A10) ----
//
// When a write (POST/PUT/DELETE) fails at the network layer, the request body
// gets stashed in localStorage so the operator can retry from the top-nav
// pending-writes pill — explicit retry, NOT an automatic offline queue.
//
// Capped at 50 entries; oldest dropped when full. Survives reload/navigation.

const PENDING_WRITES_KEY = 'protocol-pending-writes';
const PENDING_WRITES_CAP = 50;

export type PendingWrite = {
  /** Stable id (timestamp + random) — used as React key + retry/dismiss handle. */
  id: string;
  method: 'POST' | 'PUT' | 'DELETE';
  path: string;
  /** Original body — `undefined` for DELETE bodyless requests. */
  body: unknown;
  /** ISO string. */
  attempted_at: string;
};

// In-memory subscriber list so the top-nav indicator re-renders whenever the
// stash changes. Mirrors the pattern in `lib/auth.ts`.
const pendingListeners = new Set<() => void>();
function emitPending() {
  for (const l of pendingListeners) l();
}

export function subscribePendingWrites(cb: () => void): () => void {
  pendingListeners.add(cb);
  return () => {
    pendingListeners.delete(cb);
  };
}

// Cached snapshot for `useSyncExternalStore`. Returning a freshly-allocated
// array every call (as `getPendingWrites()` does, since JSON.parse+filter
// always allocates) causes React to treat every render as a state change and
// triggers a max-update-depth crash. The cached reference only swaps when
// `writePending` runs or `refreshPendingFromStorage` is called.
let cachedPendingSnapshot: PendingWrite[] = readPendingFromStorage();

export function getPendingWritesSnapshot(): PendingWrite[] {
  return cachedPendingSnapshot;
}

/** Re-read from localStorage and notify subscribers. Use from `storage` events. */
export function refreshPendingFromStorage(): void {
  cachedPendingSnapshot = readPendingFromStorage();
  emitPending();
}

function readPendingFromStorage(): PendingWrite[] {
  if (typeof localStorage === 'undefined') return [];
  return getPendingWrites();
}

export function getPendingWrites(): PendingWrite[] {
  try {
    const raw = localStorage.getItem(PENDING_WRITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Defensive shape check — a corrupt entry from an older build shouldn't
    // crash the app; we drop unknown shapes silently.
    return parsed.filter(
      (e): e is PendingWrite =>
        e !== null &&
        typeof e === 'object' &&
        typeof (e as PendingWrite).id === 'string' &&
        typeof (e as PendingWrite).path === 'string' &&
        typeof (e as PendingWrite).attempted_at === 'string' &&
        (['POST', 'PUT', 'DELETE'] as const).includes((e as PendingWrite).method),
    );
  } catch {
    return [];
  }
}

function writePending(list: PendingWrite[]): void {
  try {
    localStorage.setItem(PENDING_WRITES_KEY, JSON.stringify(list));
    cachedPendingSnapshot = list;
    emitPending();
  } catch {
    // Quota / private-mode — best-effort; the inline error stays visible
    // regardless, so the operator still sees the failure.
  }
}

function stashPendingWrite(args: {
  method: 'POST' | 'PUT' | 'DELETE';
  path: string;
  body: unknown;
}): void {
  const entry: PendingWrite = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method: args.method,
    path: args.path,
    body: args.body,
    attempted_at: new Date().toISOString(),
  };
  const next = [...getPendingWrites(), entry];
  // Cap: drop oldest when over. Plan A10: 50.
  while (next.length > PENDING_WRITES_CAP) next.shift();
  writePending(next);
}

export function dismissPendingWrite(id: string): void {
  writePending(getPendingWrites().filter((e) => e.id !== id));
}

export function clearPendingWrites(): void {
  writePending([]);
}

/**
 * Retry a stashed write. On success the entry is removed; on failure it stays
 * (apiClient re-stashes on a fresh `ApiNetworkError`, but `stashPendingWrite`
 * is idempotent in effect — the operator just sees one entry for the next
 * retry). Returns the awaited response so the caller can refresh queries.
 */
export async function retryPendingWrite(id: string): Promise<unknown> {
  const entry = getPendingWrites().find((e) => e.id === id);
  if (!entry) return undefined;
  // Remove eagerly — if the retry fails the request layer re-stashes a fresh
  // entry (new id, new attempted_at) so we don't end up with duplicates.
  dismissPendingWrite(id);
  switch (entry.method) {
    case 'POST':
      return apiClient.post(entry.path, entry.body);
    case 'PUT':
      return apiClient.put(entry.path, entry.body);
    case 'DELETE':
      return apiClient.delete(entry.path);
  }
}
